
import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiForgotPassword, ApiVerifyOtp } from "../../Core/Apicall";

function VerifyOtp() {
    const navigate = useNavigate();
    const location = useLocation();
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [countdown, setCountdown] = useState(30);
    const [isResending, setIsResending] = useState(false);
    const [otpSent, setOtpSent] = useState(false);

    useEffect(() => {
        if (countdown <= 0) return;

        const timer = setTimeout(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdown]);


    const handleResendOtp = async () => {
        if (countdown > 0 || isResending) return;

        try {
            setIsResending(true);

            const res = await ApiForgotPassword({ email });

            if (!res?.status) {
                throw new Error("Unable to resend OTP");
            }

            formik.setFieldValue("otp", "");
            formik.setFieldTouched("otp", false);
            inputRefs.current[0]?.focus();

            setCountdown(30);
            setOtpSent(true);

            toast.success("A new OTP has been sent to your email");
        } catch (error: any) {
            toast.error(error?.message || "Failed to resend OTP");
        } finally {
            setIsResending(false);
        }
    };

    const email = location.state?.email;

    const formik = useFormik({
        initialValues: { otp: "" },
        validationSchema: Yup.object({
            otp: Yup.string()
                .matches(/^\d{6}$/, "Please enter all 6 digits")
                .required("OTP is required"),
        }),
        onSubmit: async (values) => {
            try {
                const res = await ApiVerifyOtp({
                    email,
                    otp: values.otp,
                });

                if (!res?.status) {
                    throw new Error("Invalid or expired OTP");
                }

                navigate("/reset-password", {
                    state: { email, otp: values.otp },
                });
            } catch (error: any) {
                toast.error(error?.message || "OTP verification failed");
            }
        },
    });

    React.useEffect(() => {
        if (!email) {
            navigate("/forgot-password", { replace: true });
        }
    }, [email, navigate]);

    if (!email) return null;

    const otp = formik.values.otp.padEnd(6, " ").split("").slice(0, 6);
    const hasError = formik.touched.otp && Boolean(formik.errors.otp);

    const updateOtp = (nextOtp: string) => {
        formik.setFieldValue("otp", nextOtp.slice(0, 6));
    };

    const handleChange = (
        value: string,
        index: number
    ) => {
        const digits = value.replace(/\D/g, "");

        // Support pasting the complete OTP into any box.
        if (digits.length > 1) {
            const pastedOtp = digits.slice(0, 6);
            updateOtp(pastedOtp);

            const nextIndex = Math.min(pastedOtp.length, 5);
            inputRefs.current[nextIndex]?.focus();
            return;
        }

        const currentOtp = formik.values.otp.split("");
        currentOtp[index] = digits;

        updateOtp(
            currentOtp.join("").slice(0, 6)
        );

        if (digits && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (event.key === "Backspace") {
            if (!otp[index].trim() && index > 0) {
                const currentOtp = formik.values.otp.split("");
                currentOtp[index - 1] = "";

                updateOtp(currentOtp.join(""));
                inputRefs.current[index - 1]?.focus();
            } else {
                const currentOtp = formik.values.otp.split("");
                currentOtp[index] = "";
                updateOtp(currentOtp.join(""));
            }

            event.preventDefault();
        }

        if (event.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }

        if (event.key === "ArrowRight" && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {
        event.preventDefault();

        const pastedOtp = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, 6);

        if (!pastedOtp) return;

        updateOtp(pastedOtp);

        const nextIndex = Math.min(pastedOtp.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    return (
        <Box className="p-3 p-lg-5 d-flex align-items-center justify-content-center h-100 w-100">
            <Box
                className="p-3 p-lg-5 d-flex flex-column w-100 border-10"
                sx={{
                    maxWidth: 700,
                    border: { xs: "none", md: "1px solid #ccc" },
                }}
            >
                <Typography variant="h5" className="text-center fw-bold">
                    Verify OTP
                </Typography>

                <Typography className="text-center text-grey mt-2 mb-4">
                    Enter the 6-digit OTP sent to {email}.
                </Typography>

                <Box component="form" onSubmit={formik.handleSubmit}>
                    <Typography className="fw-bold mb-3">
                        Enter OTP
                    </Typography>

                    <Box
                        className="d-flex justify-content-center gap-2"
                        sx={{ width: "100%" }}
                    >
                        {otp.map((digit, index) => (
                            <TextField
                                key={index}
                                inputRef={(element) => {
                                    inputRefs.current[index] = element;
                                }}
                                value={digit.trim()}
                                onChange={(event) =>
                                    handleChange(event.target.value, index)
                                }
                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                                    handleKeyDown(event, index)
                                }
                                onPaste={handlePaste}
                                onBlur={() => formik.setFieldTouched("otp", true)}
                                error={Boolean(hasError)}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 6,
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                        "aria-label": `OTP digit ${index + 1}`,
                                        style: {
                                            textAlign: "center",
                                            fontSize: "22px",
                                            fontWeight: 600,
                                            padding: "12px 0",
                                        },
                                    },
                                }}
                                sx={{
                                    width: { xs: "42px", sm: "52px" },
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                    },
                                }}
                            />
                        ))}
                    </Box>

                    {hasError && (
                        <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 1, display: "block" }}
                        >
                            {formik.errors.otp}
                        </Typography>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        className="btn-main border-10 mt-4"
                        sx={{ py: 1.5 }}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? "Verifying..." : "Verify OTP"}
                    </Button>
                </Box>

                <Box className="d-flex justify-content-center align-items-center mt-3 gap-1">
                    <Typography variant="body2" className="text-grey">
                        Didn't receive the OTP?
                    </Typography>

                    <Button
                        variant="text"
                        onClick={handleResendOtp}
                        disabled={countdown > 0 || isResending}
                        className={`${countdown > 0 ? " " : "text-main"}`}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            minWidth: "auto",
                            p: 0.5,
                        }}
                    >
                        {isResending
                            ? "Sending..."
                            : countdown > 0
                                ? `Resend OTP (${countdown}s)`
                                : "Resend OTP"}
                    </Button>
                </Box>

                <Typography className="text-center mt-4">
                    <Link
                        to="/forgot-password"
                        state={{ email }}
                        className="text-main"
                    >
                        Change email
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
}

export default VerifyOtp;
